import React, { useState } from 'react';
import { Calculator, Zap, IndianRupee, Leaf } from 'lucide-react';

const SolarCalculator = () => {
  const [monthlyBill, setMonthlyBill] = useState('');
  const [roofArea, setRoofArea] = useState('');
  const [results, setResults] = useState(null);

  const calculate = () => {
    const bill = parseFloat(monthlyBill) || 0;
    const area = parseFloat(roofArea) || 0;

    if (bill <= 0 && area <= 0) return;

    // Calculations based on standard industry assumptions
    const ratePerUnit = 8; // Avg commercial tariff in INR/kWh
    const monthlyConsumption = bill / ratePerUnit; // kWh per month
    const annualConsumption = monthlyConsumption * 12;

    // Solar capacity: ~10 sqm per kW, ~1500 kWh/kW/year in India
    const maxCapacityFromArea = area > 0 ? area / 10 : 999;
    const requiredCapacity = annualConsumption / 1500;
    const recommendedCapacity = Math.min(requiredCapacity, maxCapacityFromArea);
    const roundedCapacity = Math.ceil(recommendedCapacity * 10) / 10;

    // Financial estimates
    const costPerKw = 55000; // INR per kW installed
    const totalInvestment = roundedCapacity * costPerKw;
    const annualGeneration = roundedCapacity * 1500;
    const annualSaving = annualGeneration * ratePerUnit;
    const monthlySaving = annualSaving / 12;
    const paybackYears = totalInvestment / annualSaving;
    const co2Saved = annualGeneration * 0.82 / 1000; // tons per year
    const lifetimeSavings = annualSaving * 25; // 25-year plant life

    setResults({
      capacity: roundedCapacity.toFixed(1),
      investment: Math.round(totalInvestment),
      monthlySaving: Math.round(monthlySaving),
      annualSaving: Math.round(annualSaving),
      payback: paybackYears.toFixed(1),
      co2Saved: co2Saved.toFixed(1),
      lifetimeSavings: Math.round(lifetimeSavings),
      annualGeneration: Math.round(annualGeneration)
    });
  };

  const formatINR = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    return num.toLocaleString('en-IN');
  };

  return (
    <section className="py-20 md:py-32 bg-surface" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-16">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block" data-testid="calc-overline">
            Savings Estimator
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight text-primary mb-4" data-testid="calc-title">
            Solar Savings Calculator
          </h2>
          <p className="text-base leading-relaxed text-text_muted max-w-3xl mx-auto">
            Estimate your potential savings with a C&I solar power plant. Enter your details below to get an instant ROI analysis.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white p-8 rounded-sm border border-border" data-testid="calc-inputs">
              <div className="flex items-center mb-6">
                <Calculator className="text-accent mr-3" size={28} />
                <h3 className="text-xl font-semibold text-primary">Enter Your Details</h3>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Monthly Electricity Bill (INR)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text_muted font-medium">INR</span>
                    <input
                      type="number"
                      value={monthlyBill}
                      onChange={(e) => setMonthlyBill(e.target.value)}
                      placeholder="e.g., 500000"
                      className="w-full pl-14 pr-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      data-testid="calc-input-bill"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Available Roof Area (sq. meters)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text_muted font-medium">sqm</span>
                    <input
                      type="number"
                      value={roofArea}
                      onChange={(e) => setRoofArea(e.target.value)}
                      placeholder="e.g., 1000"
                      className="w-full pl-14 pr-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      data-testid="calc-input-area"
                    />
                  </div>
                </div>

                <button
                  onClick={calculate}
                  className="w-full px-6 py-4 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all hover:-translate-y-0.5 shadow-md"
                  data-testid="calc-submit-button"
                >
                  Calculate Savings
                </button>

                <p className="text-xs text-text_muted leading-relaxed">
                  * Estimates are based on average commercial tariff of INR 8/kWh, 1500 kWh/kW/year generation, 
                  and INR 55,000/kW installation cost. Actual values may vary based on location, usage pattern, and system design.
                </p>
              </div>
            </div>

            {/* Results Section */}
            <div data-testid="calc-results">
              {results ? (
                <div className="bg-white p-8 rounded-sm border border-border">
                  <h3 className="text-xl font-semibold text-primary mb-6">Your Solar Savings Estimate</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-accent/5 p-4 rounded border border-accent/20" data-testid="result-capacity">
                      <div className="flex items-center mb-2">
                        <Zap size={18} className="text-accent mr-2" />
                        <span className="text-xs font-semibold text-text_muted uppercase">Recommended Capacity</span>
                      </div>
                      <p className="text-2xl font-bold text-primary">{results.capacity} kW</p>
                      <p className="text-xs text-text_muted mt-1">{results.annualGeneration.toLocaleString('en-IN')} kWh/year</p>
                    </div>

                    <div className="bg-teal-50 p-4 rounded border border-teal-200" data-testid="result-monthly">
                      <div className="flex items-center mb-2">
                        <IndianRupee size={18} className="text-teal-600 mr-2" />
                        <span className="text-xs font-semibold text-text_muted uppercase">Monthly Saving</span>
                      </div>
                      <p className="text-2xl font-bold text-teal-700">INR {formatINR(results.monthlySaving)}</p>
                      <p className="text-xs text-text_muted mt-1">INR {formatINR(results.annualSaving)} / year</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-border">
                      <span className="text-text_muted">Estimated Investment</span>
                      <span className="font-semibold text-primary" data-testid="result-investment">INR {formatINR(results.investment)}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-border">
                      <span className="text-text_muted">Payback Period</span>
                      <span className="font-semibold text-primary" data-testid="result-payback">{results.payback} years</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-border">
                      <span className="text-text_muted flex items-center">
                        <Leaf size={16} className="mr-1 text-teal-600" /> CO2 Offset / Year
                      </span>
                      <span className="font-semibold text-teal-700" data-testid="result-co2">{results.co2Saved} tons</span>
                    </div>
                    <div className="flex justify-between py-3 bg-accent/5 px-4 rounded">
                      <span className="font-semibold text-primary">25-Year Lifetime Savings</span>
                      <span className="font-bold text-accent text-lg" data-testid="result-lifetime">INR {formatINR(results.lifetimeSavings)}</span>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-primary/5 rounded border border-primary/10">
                    <p className="text-sm text-primary font-medium mb-1">Want a detailed analysis?</p>
                    <p className="text-xs text-text_muted">
                      Contact us for a free site survey and detailed techno-commercial proposal tailored to your facility.
                    </p>
                    <a
                      href="/contact"
                      className="inline-block mt-3 px-5 py-2 bg-accent text-white rounded text-sm font-semibold hover:bg-opacity-90 transition-all"
                      data-testid="calc-contact-cta"
                    >
                      Request Free Survey
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-8 rounded-sm border border-border flex flex-col items-center justify-center h-full text-center">
                  <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mb-6">
                    <Calculator size={36} className="text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-3">Get Your Solar ROI</h3>
                  <p className="text-text_muted leading-relaxed max-w-sm">
                    Enter your monthly electricity bill and available roof area to see how much you can save with solar power.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolarCalculator;
